/**
 * The procedural rotary-engine synth — **parked, not deleted, and never
 * selected at runtime.**
 *
 * A detuned sawtooth cluster (the multi-cylinder growl) → lowpass → tremolo at
 * the cylinder-firing rate → overall health-scaled gain, driven every frame
 * from `continuous-params.ts`'s curves. It was the port's engine layer until a
 * live OPL-register capture of `RB.EXE` settled what the original actually does
 * (`docs/formats/sound-music.md` § "Continuous engine sound",
 * `docs/engineering/audio-plan.md` §6): one OPL2 voice on bank program 4, keyed
 * on once and pitch-swept, with **no** gain modulation, **no** tremolo layer and
 * **no** cylinder-firing modulator — none of which this circuit can be tuned
 * into being. `engine-voice-dsp.ts` runs that real voice instead.
 *
 * It survives, unwired behind `ENGINE_SYNTH_ENABLED`, for one reason: its
 * mix-level tuning (`continuous-params.ts`'s `ENGINE_SYNTH_LEVEL` and the
 * throttle/health gain curves) was *measured* against the rest of the mix — see
 * `audio-plan.md`'s §3 amendment — and re-deriving that would be the expensive
 * part if a stylised "modern engine" option is ever wanted alongside the
 * authentic one. Its nodes are still built (so the circuit cannot rot into
 * something that no longer compiles or connects) but never started, so it
 * contributes exactly nothing to the graph's output.
 *
 * It is emphatically **not** the `AudioWorklet` fallback: see
 * `engine-worklet-node.ts` for why that path is silence instead.
 */

import {
  engineCylinderFireHz,
  engineDetuneCents,
  engineFilterHz,
  engineGrowlHz,
  engineOverallGain,
  ENGINE_TREMOLO_DEPTH,
} from "./continuous-params.ts";

/** Flip to `true` only to A/B the parked circuit by hand; nothing in the port sets it. */
const ENGINE_SYNTH_ENABLED = false;

/** Relative detune multiplier per cluster oscillator — symmetric around the base pitch (0 = unison). */
const ENGINE_OSC_DETUNE_SPREAD: readonly number[] = [-1, 0, 1];

export interface EngineSynthHandle {
  readonly overallGain: GainNode;
  readonly oscillators: readonly OscillatorNode[];
  readonly filter: BiquadFilterNode;
  readonly tremoloLfo: OscillatorNode;
}

export function createEngineSynth(context: AudioContext, bus: GainNode): EngineSynthHandle {
  const mixGain = context.createGain();
  mixGain.gain.value = 1 / ENGINE_OSC_DETUNE_SPREAD.length;

  const oscillators = ENGINE_OSC_DETUNE_SPREAD.map(() => {
    const osc = context.createOscillator();
    osc.type = "sawtooth";
    osc.connect(mixGain);
    if (ENGINE_SYNTH_ENABLED) osc.start();
    return osc;
  });

  const filter = context.createBiquadFilter();
  filter.type = "lowpass";
  mixGain.connect(filter);

  // Standard WebAudio tremolo circuit: a sine LFO through a depth-scaled gain, summed onto the
  // target gain's own baseline value (1) — the AudioParam adds the incoming signal rather than
  // multiplying it, so the *baseline* has to already be 1 for the sum to land on ±depth.
  const tremoloGain = context.createGain();
  tremoloGain.gain.value = 1;
  filter.connect(tremoloGain);
  const tremoloLfo = context.createOscillator();
  tremoloLfo.type = "sine";
  const tremoloDepth = context.createGain();
  tremoloDepth.gain.value = ENGINE_TREMOLO_DEPTH;
  tremoloLfo.connect(tremoloDepth);
  tremoloDepth.connect(tremoloGain.gain);
  if (ENGINE_SYNTH_ENABLED) tremoloLfo.start();

  const overallGain = context.createGain();
  overallGain.gain.value = 0;
  tremoloGain.connect(overallGain);
  overallGain.connect(bus);

  return { overallGain, oscillators, filter, tremoloLfo };
}

export function updateEngineSynth(handle: EngineSynthHandle, throttle: number, engineHealth: number): void {
  if (!ENGINE_SYNTH_ENABLED) return;
  const growlHz = engineGrowlHz(throttle);
  const detuneCents = engineDetuneCents(engineHealth);
  handle.oscillators.forEach((osc, index) => {
    osc.frequency.value = growlHz;
    osc.detune.value = (ENGINE_OSC_DETUNE_SPREAD[index] ?? 0) * detuneCents;
  });
  handle.filter.frequency.value = engineFilterHz(throttle);
  handle.tremoloLfo.frequency.value = engineCylinderFireHz(throttle);
  handle.overallGain.gain.value = engineOverallGain(throttle, engineHealth);
}

export function disposeEngineSynth(handle: EngineSynthHandle): void {
  if (!ENGINE_SYNTH_ENABLED) return;
  for (const osc of handle.oscillators) osc.stop();
  handle.tremoloLfo.stop();
}
